import { Component, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { can } from "../lib/roles";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet";
import {
  Activity, BookOpen, Brain, Car, Clapperboard, FileSearch, FileText, Film, Image, Layers, ListChecks, Loader2,
  Music, ScanSearch, SpellCheck, Table, Users, Wand2, X, ZoomIn,
} from "lucide-react";

// Tâches en cours (cf-1vy). Deux morceaux :
// · ActiveJobsPoller (export par défaut) : monté UNE fois hors des routes (App.js), connecté seulement, sans rendu ;
//   interroge /notifications/active-jobs et publie le résultat dans un petit store partagé — il survit aux changements
//   de page (chaque page monte son propre AppLayout).
// · ActiveJobsTrigger : dans le cadre de l'app, JAMAIS par-dessus le contenu — pied du menu latéral à côté de la cloche
//   (ordinateur : Popover à droite du menu) et barre du haut (téléphone : feuille en bas, pleine largeur). Caché quand
//   rien ne tourne. Ne bloque jamais la page : premier appel différé, erreurs silencieuses (on réessaie au tour
//   suivant), aucun appel quand l'onglet est caché.
export const POLL_ACTIVE_MS = 10000;
export const POLL_IDLE_MS = 60000;
export const FIRST_POLL_DELAY_MS = 1500;   // après le rendu de la page, jamais en concurrence avec son chargement
const MIN_GAP_MS = 5000;                     // changements de page en rafale : un appel au plus toutes les 5 s

const GENERATION_KINDS = new Set(["toolbox", "production"]);
const ICONS = {
  structuring: FileText,
  breakdown_table: Table,
  video_blocks: Clapperboard,
  script_audit: SpellCheck,
  script_understanding: Brain,
  bible_detection: ScanSearch,
  vehicle_reclass: Car,
  bible_sheets: BookOpen,
  byteplus_characters: Users,
  brief_analysis: FileSearch,
  review_prep: ListChecks,
  generation_video: Film,
  generation_multishot: Layers,
  generation_image: Image,
  generation_audio: Music,
  generation_upscale: ZoomIn,
  generation_bible: BookOpen,
  generation_other: Wand2,
};
const KNOWN_LABELS = new Set(Object.keys(ICONS));

// Section de rôle de chaque page cible (= `section` de son ProtectedRoute dans App.js) : un rôle qui ne peut pas l'ouvrir
// serait renvoyé au tableau de bord — la ligne reste listée, sans lien.
const LINK_SECTIONS = { "scenario-v2": "scenario", analysis: "brief", toolbox: "toolbox", production: "production" };

export function sectionOfLink(link) {
  const path = String(link || "").split(/[?#]/)[0];
  if (path === "/toolbox") return "toolbox";
  const m = /^\/projects\/[^/]+\/([a-z0-9-]+)/.exec(path);
  return m ? LINK_SECTIONS[m[1]] || null : null;
}

export function canOpenLink(user, link) {
  if (!link) return false;
  const section = sectionOfLink(link);
  return !section || can(user, section);
}

// ── Store partagé (un poller, plusieurs déclencheurs) ──
const EMPTY = { jobs: null, refreshing: false, user: null };
let store = EMPTY;
const listeners = new Set();
let pollSoonFn = null;

function setStore(patch) {
  store = { ...store, ...patch };
  listeners.forEach((l) => l());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** { jobs (null = jamais chargé), refreshing, user } publiés par le poller. */
export function useActiveJobs() {
  return useSyncExternalStore(subscribe, () => store, () => EMPTY);
}

/** Demande un appel bientôt (écart minimal de 5 s respecté) : ouverture du panneau, nouvelle page. */
export function refreshActiveJobs() {
  pollSoonFn?.();
}

/** Tests : remet le store à zéro. */
export function resetActiveJobsStore() {
  store = EMPTY;
  pollSoonFn = null;
  listeners.forEach((l) => l());
}

/** Id du projet de la page (`/projects/:id/...`), ou null. Le dock vit hors des routes : pas de useParams. */
export function projectIdFromPath(pathname) {
  const m = /^\/projects\/([^/?#]+)/.exec(pathname || "");
  if (!m || m[1] === "new") return null;
  try { return decodeURIComponent(m[1]); } catch { return m[1]; }
}

/** Groupes par projet, le projet courant d'abord puis le plus récemment lancé. Les générations d'un même type vers la
 *  même page sont réunies en une ligne (« Vidéo ×12 », depuis la plus ancienne) ; les jobs texte restent une ligne. */
export function groupJobs(jobs, currentProjectId) {
  const groups = new Map();
  for (const j of Array.isArray(jobs) ? jobs : []) {
    if (!j || !j.id) continue;
    const pid = j.project_id || "";
    let g = groups.get(pid);
    if (!g) {
      g = { projectId: pid || null, projectName: j.project_name || "", rows: [], byKey: new Map(), latest: "" };
      groups.set(pid, g);
    }
    const started = String(j.started_at || "");
    if (started > g.latest) g.latest = started;
    const label = KNOWN_LABELS.has(j.label_code) ? j.label_code : "generation_other";
    const isGeneration = GENERATION_KINDS.has(j.kind);
    const key = isGeneration ? `gen|${label}|${j.link || ""}` : j.id;
    const row = g.byKey.get(key);
    if (row) {
      row.count += 1;
      if (started && (!row.startedAt || started < row.startedAt)) row.startedAt = started;
      if (j.status !== "pending") row.status = "running";
      continue;
    }
    const fresh = {
      key, kind: j.kind || "", label, status: j.status === "pending" ? "pending" : "running",
      progress: j.progress && typeof j.progress === "object" ? j.progress : null,
      startedAt: started, link: j.link || null, count: 1, byName: j.started_by_name || null,
    };
    g.byKey.set(key, fresh);
    g.rows.push(fresh);
  }
  return [...groups.values()]
    .sort((a, b) => {
      const ac = currentProjectId && a.projectId === currentProjectId ? 1 : 0;
      const bc = currentProjectId && b.projectId === currentProjectId ? 1 : 0;
      if (ac !== bc) return bc - ac;
      return b.latest.localeCompare(a.latest);
    })
    .map(({ byKey, latest, ...g }) => g);
}

/** Secondes écoulées depuis `startedAt` (ISO), ou null si inconnu. */
export function elapsedSeconds(startedAt, now) {
  const ms = Date.parse(startedAt || "");
  return Number.isFinite(ms) ? Math.max(0, Math.floor((now - ms) / 1000)) : null;
}

function formatElapsed(t, seconds) {
  if (seconds === null) return "";
  if (seconds < 60) return t("activeJobs.elapsed.seconds", { s: seconds });
  if (seconds < 3600) return t("activeJobs.elapsed.minutes", { m: Math.floor(seconds / 60) });
  const m = Math.floor((seconds % 3600) / 60);
  return t("activeJobs.elapsed.hours", { h: Math.floor(seconds / 3600), m: String(m).padStart(2, "0") });
}

function progressOf(row) {
  const p = row.progress;
  if (!p) return null;
  if (Number.isFinite(p.pct)) return { pct: Math.max(0, Math.min(100, p.pct)) };
  if (Number.isFinite(p.done) && Number.isFinite(p.total) && p.total > 0) {
    return { pct: Math.round((Math.min(p.done, p.total) / p.total) * 100), done: p.done, total: p.total };
  }
  return null;
}

function JobRow({ row, now, user, onNavigate }) {
  const { t } = useTranslation();
  const Icon = ICONS[row.label] || Activity;
  const prog = progressOf(row);
  let detail = "";
  if (row.status === "pending") detail = t("activeJobs.status.pending");
  else if (prog && prog.total) {
    detail = t(row.kind === "video_blocks" ? "activeJobs.progress.launching" : "activeJobs.progress.count",
      { done: prog.done, total: prog.total });
  } else if (prog) detail = t("activeJobs.progress.pct", { pct: prog.pct });
  const elapsed = formatElapsed(t, elapsedSeconds(row.startedAt, now));
  const body = (
    <>
      <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-violet-500/10 text-violet-300">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="truncate text-[13px] text-neutral-100">
            {t(`activeJobs.kinds.${row.label}`)}
            {row.count > 1 && <span className="ml-1 text-neutral-400">{t("activeJobs.times", { count: row.count })}</span>}
            {row.byName && <span className="ml-1.5 text-[11px] text-neutral-500" data-testid="job-by">{t("activeJobs.by", { name: row.byName })}</span>}
          </span>
          <span className="ml-auto flex-shrink-0 font-mono text-[11px] tabular-nums text-neutral-500" data-testid="job-elapsed">{elapsed}</span>
        </span>
        <span className="mt-1.5 flex items-center gap-2">
          <span className="relative h-1 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
            {prog ? (
              <span className="absolute inset-y-0 left-0 rounded-full bg-violet-500 transition-[width] duration-500" style={{ width: `${prog.pct}%` }} />
            ) : (
              <span className="absolute inset-y-0 left-0 w-1/3 animate-pulse rounded-full bg-violet-500/50" />
            )}
          </span>
          {detail && <span className="flex-shrink-0 text-[11px] tabular-nums text-neutral-400">{detail}</span>}
        </span>
      </span>
    </>
  );
  const cls = "flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left transition-colors";
  if (!canOpenLink(user, row.link)) return <li className={cls} data-testid="job-row">{body}</li>;
  return (
    <li data-testid="job-row">
      <a
        href={row.link}
        onClick={(e) => { e.preventDefault(); onNavigate(row.link); }}
        className={`${cls} hover:bg-white/[0.04] focus:outline-none focus-visible:ring-1 focus-visible:ring-violet-500`}
      >
        {body}
      </a>
    </li>
  );
}

/** Contenu du panneau ouvert. `jobs` null = jamais chargé : squelette. */
/** Contenu du panneau (dans le Popover ou la feuille). `jobs` null = jamais chargé : squelette. `Title` : l'élément du
 *  titre (SheetTitle dans la feuille : la boîte Radix le relie elle-même) ; `titleId` : pour l'aria-labelledby du
 *  Popover. Fermeture : bouton « Replier » (Popover) — la feuille a sa propre croix. */
export function ActiveJobsPanel({
  jobs, currentProjectId, now, refreshing, user, onNavigate, onClose, titleId, Title = "h2", showClose = true,
}) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupJobs(jobs, currentProjectId), [jobs, currentProjectId]);
  const count = Array.isArray(jobs) ? jobs.length : 0;
  return (
    <div
      data-testid="active-jobs-panel"
      className="flex max-h-[min(70vh,520px)] w-full flex-col overflow-hidden rounded-t-xl border border-white/[0.08] bg-[#0D0D0D]/95 shadow-2xl shadow-black/60 backdrop-blur-xl sm:rounded-xl"
    >
      <header className={`flex items-center gap-2 border-b border-white/[0.06] py-2.5 pl-3 ${showClose ? "pr-3" : "pr-12"}`}>
        <Activity className="h-4 w-4 text-violet-400" aria-hidden="true" />
        <Title {...(titleId ? { id: titleId } : {})} className="text-[13px] font-medium text-neutral-100">{t("activeJobs.title")}</Title>
        {count > 0 && <span className="rounded-full bg-violet-600/20 px-1.5 text-[11px] tabular-nums text-violet-200">{count}</span>}
        {refreshing && <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-500" aria-label={t("activeJobs.refreshing")} />}
        {showClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t("activeJobs.close")}
            className="ml-auto rounded-md p-1 text-neutral-400 hover:bg-white/[0.06] hover:text-neutral-100"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </header>
      <div className="flex-1 overflow-y-auto overscroll-contain px-1.5 py-1.5">
        {jobs === null ? (
          <ul aria-busy="true" aria-label={t("activeJobs.loading")} data-testid="active-jobs-skeleton" className="space-y-1 p-1">
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex items-center gap-2.5 px-1 py-1.5">
                <span className="h-7 w-7 animate-pulse rounded-md bg-white/[0.06]" />
                <span className="flex-1 space-y-1.5">
                  <span className="block h-2.5 w-2/3 animate-pulse rounded bg-white/[0.06]" />
                  <span className="block h-1 w-full animate-pulse rounded bg-white/[0.04]" />
                </span>
              </li>
            ))}
          </ul>
        ) : !groups.length ? (
          <p className="px-3 py-6 text-center text-[13px] text-neutral-500" data-testid="active-jobs-empty">{t("activeJobs.empty")}</p>
        ) : (
          groups.map((g) => (
            <div key={g.projectId || "_none"} className="mb-1 last:mb-0" data-testid="job-group">
              <div className="flex items-center gap-2 px-2 pb-0.5 pt-2">
                <span className="truncate text-[11px] font-medium uppercase tracking-wide text-neutral-400" data-testid="job-group-name">
                  {g.projectId ? (g.projectName || t("activeJobs.untitledProject")) : t("activeJobs.noProject")}
                </span>
                {currentProjectId && g.projectId === currentProjectId && (
                  <span className="flex-shrink-0 rounded bg-violet-600/20 px-1 text-[10px] text-violet-200">{t("activeJobs.currentProject")}</span>
                )}
              </div>
              <ul>{g.rows.map((row) => <JobRow key={row.key} row={row} now={now} user={user} onNavigate={onNavigate} />)}</ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/** Monté hors de l'ErrorBoundary de l'app : une erreur du dock ne doit jamais démonter la page — il disparaît seul. */
class DockBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.error("[ActiveJobsDock] masqué après une erreur :", err); }
  render() { return this.state.failed ? null : this.props.children; }
}

export default function ActiveJobsPoller(props) {
  return <DockBoundary><ActiveJobsPollerInner {...props} /></DockBoundary>;
}

export function ActiveJobsTrigger(props) {
  return <DockBoundary><ActiveJobsTriggerInner {...props} /></DockBoundary>;
}

function ActiveJobsPollerInner({ api, userId, user }) {
  const { pathname } = useLocation();
  const setJobs = (jobs) => setStore({ jobs });
  const setRefreshing = (refreshing) => setStore({ refreshing });

  // L'utilisateur (droits des liens) ; changement de compte : rien de l'ancien ne reste affiché
  useEffect(() => { setStore({ user: user || null }); }, [user]);
  useEffect(() => { setStore({ jobs: null, refreshing: false }); }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    let stopped = false;
    let timer = null;
    let busy = false;
    let soon = false;   // relecture demandée pendant un appel : il a pu lire l'état d'avant le lancement
    let running = 0;
    let lastPollAt = 0;
    const hidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";
    const clear = () => { clearTimeout(timer); timer = null; };
    const schedule = (ms) => {
      clear();
      if (stopped || hidden()) return;   // onglet caché : plus aucun appel, la reprise vient de visibilitychange
      timer = setTimeout(poll, ms);
    };

    async function poll() {
      timer = null;
      if (stopped || busy || hidden()) return;
      busy = true;
      lastPollAt = Date.now();
      setRefreshing(true);
      try {
        const { data } = await axios.get(`${api}/notifications/active-jobs`, { timeout: 15000 });
        if (stopped) return;
        const list = Array.isArray(data?.jobs) ? data.jobs.filter((j) => j && j.id) : [];
        running = list.length;
        setJobs(list);
      } catch {
        // Silencieux : jamais de toast pour un indicateur ; on réessaie au tour suivant
      } finally {
        busy = false;
        if (!stopped) {
          setRefreshing(false);
          // un lancement accepté pendant cet appel : la relecture suit à l'écart minimal, pas au poll de repos (60 s)
          schedule(soon ? MIN_GAP_MS : running ? POLL_ACTIVE_MS : POLL_IDLE_MS);
          soon = false;
        }
      }
    }

    // Retour sur l'onglet : même écart minimal de 5 s qu'un changement de page (aller-retour rapide = pas d'appel en
    // rafale) ; jamais appelé encore (caché dès le montage) : le délai initial
    const onVisibility = () => {
      if (hidden()) { clear(); return; }
      if (busy) return;
      schedule(lastPollAt ? Math.max(0, lastPollAt + MIN_GAP_MS - Date.now()) : FIRST_POLL_DELAY_MS);
    };
    document.addEventListener("visibilitychange", onVisibility);
    pollSoonFn = () => {
      // avant le premier appel (redirection juste après la connexion…) : le délai initial tient, jamais avancé
      if (stopped || hidden() || !lastPollAt) return;
      if (busy) { soon = true; return; }
      schedule(Math.max(0, lastPollAt + MIN_GAP_MS - Date.now()));
    };
    schedule(FIRST_POLL_DELAY_MS);
    return () => {
      stopped = true;
      clear();
      document.removeEventListener("visibilitychange", onVisibility);
      pollSoonFn = null;
      setStore(EMPTY);   // déconnexion / changement de compte : jamais le compteur du compte précédent
    };
  }, [api, userId]);

  // Nouvelle page : un job vient peut-être d'être lancé — appel bientôt (pas au montage : le premier est déjà prévu)
  const firstPathRef = useRef(true);
  useEffect(() => {
    if (firstPathRef.current) { firstPathRef.current = false; return; }
    refreshActiveJobs();
  }, [pathname]);

  return null;
}

/** Bouton du cadre de l'app. `variant` : "sidebar" (pied du menu, Popover à droite du menu) ou "header" (barre du haut
 *  sur téléphone, feuille en bas). Caché quand rien ne tourne (gardé tant que son panneau est ouvert). Focus et Échap :
 *  ceux du Popover / de la feuille Radix (Échap ne ferme que la couche du dessus). */
function ActiveJobsTriggerInner({ variant = "sidebar" }) {
  const { t } = useTranslation();
  const { jobs, refreshing, user } = useActiveJobs();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const navigatingRef = useRef(false);
  const titleId = useId();
  const count = Array.isArray(jobs) ? jobs.length : 0;

  // Temps écoulé qui défile, seulement panneau ouvert
  useEffect(() => {
    if (!open || !count) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [open, count]);

  if (!open && !count) return null;

  const onOpenChange = (next) => {
    setOpen(next);
    if (next) { setNow(Date.now()); refreshActiveJobs(); }
  };
  const onNavigate = (link) => {
    navigatingRef.current = true;   // fermé par un lien : le focus suit la nouvelle page, pas rendu au bouton
    setOpen(false);
    navigate(link);
  };
  const onCloseAutoFocus = (e) => {
    if (navigatingRef.current) { e.preventDefault(); navigatingRef.current = false; }
  };
  const label = t("activeJobs.running", { count });
  // menu latéral : une ligne pleine largeur « N en cours » au-dessus de l'utilisateur (la rangée de l'utilisateur
  // n'a pas la place d'un troisième bouton sans masquer son nom) ; barre du haut (téléphone) : l'icône seule
  const button = variant === "sidebar" ? (
    <button
      type="button"
      aria-label={label}
      data-testid={`active-jobs-trigger-${variant}`}
      className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm text-violet-200 transition-colors hover:bg-white/[0.06]"
    >
      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-violet-400" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </button>
  ) : (
    <button
      type="button"
      aria-label={label}
      title={label}
      data-testid={`active-jobs-trigger-${variant}`}
      className="relative rounded-lg p-2 transition-colors hover:bg-white/[0.06]"
    >
      <Loader2 className="h-5 w-5 animate-spin text-violet-400" aria-hidden="true" />
      {count > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-violet-500 px-1 text-[10px] font-bold tabular-nums text-white">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
  const panelProps = {
    jobs, now, refreshing, user, onNavigate,
    currentProjectId: projectIdFromPath(pathname),
    onClose: () => setOpen(false),
  };

  if (variant === "header") {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetTrigger asChild>{button}</SheetTrigger>
        <SheetContent
          side="bottom"
          aria-describedby={undefined}
          onCloseAutoFocus={onCloseAutoFocus}
          className="border-0 bg-transparent p-0 pb-[env(safe-area-inset-bottom)] shadow-none"
        >
          <ActiveJobsPanel {...panelProps} Title={SheetTitle} showClose={false} />
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{button}</PopoverTrigger>
      <PopoverContent
        side="right"
        align="end"
        sideOffset={8}
        collisionPadding={8}
        aria-labelledby={titleId}
        onCloseAutoFocus={onCloseAutoFocus}
        className="w-[380px] border-0 bg-transparent p-0 shadow-none"
      >
        <ActiveJobsPanel {...panelProps} titleId={titleId} />
      </PopoverContent>
    </Popover>
  );
}
